import { Component } from '@theme/component';

/**
 * A custom element that manages a sticky add-to-cart button for mobile devices.
 * The button appears at the bottom of the screen when scrolling past the original add-to-cart button.
 *
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The sticky bar container
 *
 * @extends Component<StickyAddToCartRefs>
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #cloned = false;

  connectedCallback() {
    super.connectedCallback();

    // Only initialize on mobile devices
    if (window.innerWidth >= 750) {
      return;
    }

    this.#cloneBuyButtons();
    this.#initObserver();

    // Listen for window resize to handle orientation changes
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * Clone the product form with only the add-to-cart button
   */
  #cloneBuyButtons() {
    if (this.#cloned) return;

    const section = this.closest('.shopify-section');
    const productFormComponent = section?.querySelector('.buy-buttons-block product-form-component');
    const container = document.getElementById('sticky-buy-buttons-container');
    
    if (!productFormComponent || !container) return;

    // Clone the entire product-form-component
    const clone = productFormComponent.cloneNode(true);
    
    // Update IDs to avoid conflicts
    clone.querySelectorAll('[id]').forEach((el) => {
      el.id = `sticky-${el.id}`;
    });
    
    // Remove quantity selector and accelerated checkout buttons
    clone.querySelectorAll('.quantity-selector, .shopify-payment-button, .quantity-label, .quantity-rules').forEach((el) => {
      el.remove();
    });
    
    // Find the quantity input and set it to 1
    const quantityInput = clone.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = '1';
    } else {
      // If no quantity input exists, create one
      const form = clone.querySelector('form');
      if (form) {
        const newQuantityInput = document.createElement('input');
        newQuantityInput.type = 'hidden';
        newQuantityInput.name = 'quantity';
        newQuantityInput.value = '1';
        form.appendChild(newQuantityInput);
      }
    }
    
    container.appendChild(clone);
    this.#cloned = true;
  }

  /**
   * Initialize intersection observer to track original button visibility
   */
  #initObserver() {
    // Find the original buy-buttons block
    const section = this.closest('.shopify-section');
    const buyButtonsBlock = section?.querySelector('.buy-buttons-block');
    
    if (!buyButtonsBlock) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show sticky bar when original button is not visible and scrolled past
          const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          
          if (shouldShow !== this.#isVisible) {
            this.#isVisible = shouldShow;
            this.#toggleStickyBar(shouldShow);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    this.#observer.observe(buyButtonsBlock);
  }

  /**
   * Toggle sticky bar visibility
   * @param {boolean} show - Whether to show the sticky bar
   */
  #toggleStickyBar(show) {
    const { stickyBar } = this.refs;
    
    if (show) {
      stickyBar.classList.add('sticky-add-to-cart--visible');
      stickyBar.removeAttribute('hidden');
    } else {
      stickyBar.classList.remove('sticky-add-to-cart--visible');
      // Use a timeout to allow animation to complete before hiding
      setTimeout(() => {
        if (!this.#isVisible) {
          stickyBar.setAttribute('hidden', '');
        }
      }, 300);
    }
  }

  /**
   * Handle window resize events
   */
  #handleResize = () => {
    // Disable on desktop
    if (window.innerWidth >= 750) {
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }
      this.refs.stickyBar?.setAttribute('hidden', '');
      this.#isVisible = false;
    } else if (!this.#observer) {
      // Re-initialize on mobile if it was disabled
      this.#initObserver();
    }
  };
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
}